import { Injectable } from '@angular/core';
import { StudioSceneBackgroundDefinition } from '../../element-editor/store/studio/studio.types';

export interface PromptResponseError {
  path: string;
  message: string;
}

export interface PromptValidationResult<T = unknown> {
  valid: boolean;
  value?: T;
  errors: PromptResponseError[];
}

/** Partial scene descriptor returned by the AI — only the changed fields are present. */
export interface ValidatedPromptResponse {
  layoutDefinition?: unknown;
  shapes?: Record<string, unknown>;
  background?: StudioSceneBackgroundDefinition;
}

const VALID_KEYS = ['shapes', 'layoutDefinition', 'background'] as const;
const VALID_BG_TYPES = ['ellipse', 'gloves', 'gradient', 'static', 'image', 'video', 'custom-ellipse'];
const VALID_FIT_VALUES = ['cover', 'contain', 'stretch'];

@Injectable({ providedIn: 'root' })
export class PromptResponseService {
  /**
   * Parses and validates the raw JSON string returned by the AI.
   * Returns a result with errors if the format is invalid.
   */
  parse(raw: string): PromptValidationResult<ValidatedPromptResponse> {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { valid: false, errors: [{ path: '$', message: 'Response is not valid JSON' }] };
    }
    console.log('parsed', parsed);
    return this.validate(parsed);
  }

  /**
   * Validates a parsed AI response — expects a plain partial descriptor object
   * with at least one of: layoutDefinition, shapes, background.
   */
  validate(value: unknown): PromptValidationResult<ValidatedPromptResponse> {
    if (!isObject(value)) {
      return { valid: false, errors: [{ path: '$', message: 'Response must be an object' }] };
    }

    const errors: PromptResponseError[] = [];
    const obj = value as Record<string, unknown>;

    // Must contain at least one known key
    const presentKeys = Object.keys(obj).filter(k => VALID_KEYS.includes(k as any));
    if (presentKeys.length === 0) {
      errors.push({
        path: '$',
        message: `Response must contain at least one of: ${VALID_KEYS.join(', ')}`,
      });
    }

    // Warn about unexpected top-level keys
    const unknownKeys = Object.keys(obj).filter(k => !VALID_KEYS.includes(k as any));
    if (unknownKeys.length) {
      errors.push({
        path: '$',
        message: `Unexpected fields in response: ${unknownKeys.join(', ')}`,
      });
    }

    if (errors.length) return { valid: false, errors };

    // Field-specific validation
    if (obj['background'] !== undefined) {
      errors.push(...this.validateBackground(obj['background'], '$.background'));
    }
    if (obj['shapes'] !== undefined) {
      errors.push(...this.validateShapes(obj['shapes'], '$.shapes'));
    }
    // layoutDefinition: any object is accepted
    if (obj['layoutDefinition'] !== undefined && !isObject(obj['layoutDefinition'])) {
      errors.push({ path: '$.layoutDefinition', message: '"layoutDefinition" must be an object' });
    }

    if (errors.length) return { valid: false, errors };

    return {
      valid: true,
      value: obj as ValidatedPromptResponse,
      errors: [],
    };
  }

  // ── Background ──────────────────────────────────────────────────────────────

  validateBackground(value: unknown, path = '$.background'): PromptResponseError[] {
    const errors: PromptResponseError[] = [];

    if (!isObject(value)) {
      return [{ path, message: '"background" must be an object' }];
    }

    const bg = value as Record<string, unknown>;

    if (!bg['type'] || !VALID_BG_TYPES.includes(bg['type'] as string)) {
      errors.push({
        path: `${path}.type`,
        message: `"type" must be one of: ${VALID_BG_TYPES.join(', ')}. Got: ${JSON.stringify(bg['type'])}`,
      });
    }

    if (bg['type'] === 'image') {
      errors.push(...this.validateImageBgOptions(bg['image'], `${path}.image`));
    }

    return errors;
  }

  validateImageBgOptions(value: unknown, path = '$.background.image'): PromptResponseError[] {
    const errors: PromptResponseError[] = [];

    if (!isObject(value)) {
      return [{ path, message: '"image" must be an object' }];
    }

    const img = value as Record<string, unknown>;

    if (typeof img['url'] !== 'string' || !img['url']) {
      errors.push({ path: `${path}.url`, message: '"url" must be a non-empty string' });
    }

    if (img['fit'] !== undefined && !VALID_FIT_VALUES.includes(img['fit'] as string)) {
      errors.push({
        path: `${path}.fit`,
        message: `"fit" must be one of: ${VALID_FIT_VALUES.join(', ')}. Got: ${JSON.stringify(img['fit'])}`,
      });
    }

    if (img['alpha'] !== undefined) {
      if (typeof img['alpha'] !== 'number' || img['alpha'] < 0 || img['alpha'] > 1) {
        errors.push({ path: `${path}.alpha`, message: '"alpha" must be a number between 0 and 1' });
      }
    }

    if (img['scale'] !== undefined) {
      if (typeof img['scale'] !== 'number' || img['scale'] <= 0) {
        errors.push({ path: `${path}.scale`, message: '"scale" must be a positive number' });
      }
    }

    if (img['translate'] !== undefined) {
      if (
        !Array.isArray(img['translate']) ||
        img['translate'].length !== 2 ||
        typeof img['translate'][0] !== 'number' ||
        typeof img['translate'][1] !== 'number'
      ) {
        errors.push({ path: `${path}.translate`, message: '"translate" must be a tuple of two numbers [dx, dy]' });
      }
    }

    return errors;
  }

  // ── Shapes ───────────────────────────────────────────────────────────────────

  private validateShapes(value: unknown, path: string): PromptResponseError[] {
    if (!isObject(value)) {
      return [{ path, message: '"shapes" must be an object' }];
    }
    return [];
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
